import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// assets
import { General } from 'icons/untitledui';

// components
import ApiLoading from 'shared/ApiLoading';
import ErrorBoundary from 'layout/components/ErrorBoundary';
import ShareButton from 'documentDisplay/components/DocumentShareButton';
import { TopBar } from 'topBar/components/TopBar';
import type { TopBarMenuItem } from 'topBar/components/TopBar';

// hooks
import {
  useDocument,
  useDocumentDownload,
} from 'documentDisplay/hooks/documentApi';
import useBrowserDetection from 'documentDisplay/hooks/useBrowserDetection';
import { usePosthogCapture } from 'documentDisplay/posthogCapture/_index';
import { isMobile } from 'react-device-detect';

// styles
import * as PageLayout from 'layout/styles/PageLayout.style';
import * as Styled from 'documentDisplay/styles/Document.style';
import * as Text from 'theme/components/Text.style';

// constants
import routesConfig from 'constants/routesConfig.json';

interface RouteParams {
  id: string;
}

interface DocumentProps {
  document?: {
    language?: string;
    page_numbers?: number[];
    parent?: {
      id?: string;
      use_source_url?: boolean;
    };
    url?: string;
  };
  selectedPage?: number;
  resetCounter?: number;
}

export default function Document({
  document,
  selectedPage,
  resetCounter = 0,
}: DocumentProps): JSX.Element {
  const { t } = useTranslation('pageDocumentDisplay');
  const browser = useBrowserDetection();
  const documentDisplayCapture = usePosthogCapture();
  const params = useParams<RouteParams>();
  const history = useHistory();
  const { id } = params;
  const documentId = document?.parent?.id ?? id;

  const location = useLocation<{ from?: string } | undefined>();
  const queryParams = new URLSearchParams(location.search);
  const returnToParam = queryParams.get('return_to');
  const isSafeReturnTo =
    !!returnToParam &&
    returnToParam.startsWith('/') &&
    !returnToParam.startsWith('//');

  const handleBack = () => {
    if (location.state?.from) {
      history.replace(location.state.from);
    } else if (isSafeReturnTo) {
      history.replace(returnToParam);
    } else if (history.length > 1) {
      history.goBack();
    } else {
      history.replace('/');
    }
  };

  const language = queryParams.get('language');
  const pagesParam = queryParams.get('pages');
  const pagesFromQuery = pagesParam
    ? pagesParam
        .split(',')
        .map((p) => parseInt(p, 10))
        .filter((n) => !Number.isNaN(n))
    : undefined;
  const pageNumbers = pagesFromQuery || document?.page_numbers || [];
  const [pageNumber, setPageNumber] = useState<number>(
    selectedPage ||
      (pagesFromQuery && pagesFromQuery[0]) ||
      document?.page_numbers?.[0] ||
      1
  );
  const [iframeKey, setIframeKey] = useState<number>(0);

  // Update pageNumber when selectedPage changes (resetCounter ensures re-clicks work)
  useEffect(() => {
    if (selectedPage) {
      setPageNumber(selectedPage);
      setIframeKey((prev) => prev + 1);
    }
  }, [selectedPage, resetCounter]);

  const buildQueryString = () => {
    const queryParamsArr: string[] = [];
    const documentLanguage = document?.language || language;
    if (documentLanguage) queryParamsArr.push(`language=${documentLanguage}`);
    if (pageNumbers.length > 0)
      queryParamsArr.push(`pages=${pageNumbers.join(',')}`);
    return queryParamsArr.length > 0 ? `?${queryParamsArr.join('&')}` : '';
  };

  const { data: documentData, isLoading: isDocumentLoading } = useDocument(
    documentId,
    buildQueryString()
  );
  const { data: documentDownloadUrl, isLoading: isDocumentDownloadLoading } =
    useDocumentDownload(documentId, buildQueryString());

  const isLoading = isDocumentLoading || isDocumentDownloadLoading;

  const isBucketUrl = documentData?.url?.startsWith('https');
  const docPageURL = `${
    routesConfig.DOCUMENT_PAGE
  }/${documentId}${buildQueryString()}`;

  if (isLoading) return <ApiLoading />;

  const routerLink = () => {
    const useExternal =
      documentData?.parent.use_source_url && documentData?.source_url;
    let link = useExternal ? documentData?.source_url : docPageURL;

    if (!useExternal) {
      const returnTo = `${window.location.pathname}${window.location.search}`;
      const separator = link.includes('?') ? '&' : '?';
      link += `${separator}return_to=${encodeURIComponent(returnTo)}`;
    }

    return (
      <Styled.StyledLink href={link} target="_blank" rel="noopener noreferrer">
        <General.Share03 size={15} />
        {t('documentDisplay.DocumentRow.Open in new tab')}
      </Styled.StyledLink>
    );
  };

  const downloadLink = () => {
    const url = isBucketUrl ? documentData?.url : documentDownloadUrl;

    const linkProps = isBucketUrl
      ? { target: '_blank', rel: 'noopener noreferrer' }
      : { download: documentData?.title };

    return (
      <Styled.StyledLink href={url} {...linkProps}>
        <General.Download01 size={15} />
        {t('documentDisplay.DocumentRow.Open')}
      </Styled.StyledLink>
    );
  };

  const shouldUseRouterLink = (): boolean => {
    if (documentData?.parent.use_source_url && documentData?.source_url)
      return true;
    if (isMobile) return false;
    const isPdf = documentData?.url.endsWith('.pdf');
    return isBucketUrl || isPdf;
  };

  const p2PublicationDate = () => (
    <Text.P2 style={{ fontStyle: 'italic' }}>
      {t('documentDisplay.Document.Last updated doc')}{' '}
      {documentData?.publication_date}
    </Text.P2>
  );

  const handleCopyLink = () => {
    documentDisplayCapture.shared();
    const urlToCopy = `${window.location.origin}${docPageURL}`;
    navigator.clipboard
      .writeText(urlToCopy)
      .catch((err) => console.error('Failed to copy: ', err));
  };

  const handleDownload = () => {
    const url = isBucketUrl ? documentData?.url : documentDownloadUrl;
    if (!url) return;
    documentDisplayCapture.downloaded();
    const link = window.document.createElement('a');
    link.href = url;
    if (isBucketUrl) {
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
    } else if (documentData?.title) {
      link.download = documentData.title;
    }
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
  };

  const topBarMenuItems: TopBarMenuItem[] = [
    {
      label: t('documentDisplay.Document.CopyLink'),
      onClick: handleCopyLink,
      icon: <General.Copy03 size={18} />,
      successMessage: t('documentDisplay.Document.LinkCopied'),
    },
    {
      label: t('documentDisplay.Document.Download'),
      onClick: handleDownload,
      icon: <General.Download01 size={18} />,
    },
  ];

  const handleIframe = ({ onPage }: { onPage: boolean }) => {
    if (['ie'].includes(browser)) return <></>;
    if (isMobile) return <></>;
    if (documentData?.parent.use_source_url && documentData?.source_url)
      return <></>;

    const iframeSrc =
      pageNumber > 1
        ? `${documentDownloadUrl}#page=${pageNumber}`
        : documentDownloadUrl;

    return (
      <>
        <Styled.DocumentIframe
          key={`iframe-${pageNumber}-${iframeKey}`}
          src={iframeSrc}
          onPage={onPage}
        />
      </>
    );
  };

  const handlePageClick = (page: number) => {
    setPageNumber(page);
    setIframeKey((prev) => prev + 1);
  };

  const renderMostRelevant = () => {
    if (pageNumbers.length === 0) return null;
    return (
      <Styled.MostRelevantSection>
        <Styled.MostRelevantLabel>
          {t('documentDisplay.Document.MostRelevant')}
        </Styled.MostRelevantLabel>
        <Styled.PageChipsScroller>
          {pageNumbers.map((page, index) => (
            <Styled.PageChip
              key={`${page}-${index}`}
              $size="small"
              onClick={() => handlePageClick(page)}
            >
              {t('documentDisplay.DocumentList.Page', { page })}
            </Styled.PageChip>
          ))}
        </Styled.PageChipsScroller>
      </Styled.MostRelevantSection>
    );
  };

  // If displaying the document in the chat page
  if (document)
    return (
      <ErrorBoundary>
        <Styled.Header>
          {p2PublicationDate()}
          <Styled.HeaderActions>
            {shouldUseRouterLink() ? routerLink() : downloadLink()}
            <ShareButton
              docURL={docPageURL}
              useSourceUrl={documentData?.parent.use_source_url}
              sourceUrl={documentData?.source_url}
            />
          </Styled.HeaderActions>
        </Styled.Header>
        {handleIframe({ onPage: false })}
      </ErrorBoundary>
    );

  // If displaying the document in its own page
  return (
    <ErrorBoundary>
      <Styled.DocumentPageWrapper>
        <TopBar
          onBackClick={handleBack}
          showBackButton
          showOnDesktop
          backButtonAriaLabel={t('documentDisplay.Document.Back')}
          menuItems={topBarMenuItems}
          menuButtonAriaLabel={t('documentDisplay.Document.MoreActions')}
        />
        <PageLayout.ContentContainer>
          <PageLayout.Content>
            <Styled.DocumentContent>
              <Styled.DocumentTitleGroup>
                <Text.H4>{documentData?.title}</Text.H4>
                <Styled.DocumentSubtitle>
                  {t('documentDisplay.Document.Last updated doc')}{' '}
                  {documentData?.publication_date}
                </Styled.DocumentSubtitle>
              </Styled.DocumentTitleGroup>
              <Styled.Divider />
              {renderMostRelevant()}
              <Styled.DocumentViewport>
                {handleIframe({ onPage: true })}
              </Styled.DocumentViewport>
            </Styled.DocumentContent>
          </PageLayout.Content>
        </PageLayout.ContentContainer>
      </Styled.DocumentPageWrapper>
    </ErrorBoundary>
  );
}
